<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  buildDesktopNotification,
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
  showDesktopNotification,
} from 'dashboard/helper/AudioAlerts/DesktopNotificationHelper';
import Banner from 'dashboard/components/ui/Banner.vue';

const { t } = useI18n();
const router = useRouter();
const { uiSettings } = useUISettings();

const permission = ref(getDesktopNotificationPermission());
const dismissed = ref(false);

const shouldShowBanner = computed(
  () =>
    uiSettings.value.enable_desktop_popup !== false &&
    permission.value === 'default' &&
    !dismissed.value
);

const turnOn = async () => {
  permission.value = await requestDesktopNotificationPermission();
};

const showPopup = message => {
  if (getDesktopNotificationPermission() !== 'granted') return;

  const notification = buildDesktopNotification(message, {
    newMessageTitle: t(
      'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.NEW_MESSAGE'
    ),
    attachmentBody: t(
      'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.ATTACHMENT'
    ),
  });
  showDesktopNotification(notification, () => {
    window.focus();
    router.push(
      frontendURL(
        conversationUrl({
          accountId: message.account_id,
          id: message.conversation_id,
        })
      )
    );
  });
};

useEmitter(BUS_EVENTS.SHOW_DESKTOP_NOTIFICATION, showPopup);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    color-scheme="primary"
    :banner-message="
      t(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.BANNER'
      )
    "
    action-button-icon="i-lucide-bell-ring"
    :action-button-label="
      t(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.BANNER_ACTION'
      )
    "
    has-action-button
    has-close-button
    @primary-action="turnOn"
    @close="dismissed = true"
  />
</template>
